"use client"

import type {
  ComponentPropsWithoutRef,
  ComponentPropsWithRef,
  CSSProperties,
  Ref,
  SyntheticEvent,
} from "react"
import { useCallback, useMemo, useRef } from "react"
import NextImage from "next/image"
import cn from "classnames"

import { mergeRefs } from "~/functions/mergeRefs"
import type { Target } from "~/styles/exports"
import { BREAKPOINTS } from "~/styles/exports"
import type { Image as ImageType } from "~/types/asset"

import type { SharedAssetProps } from "./Asset"
import s from "./Asset.module.scss"

const DEFAULT_IMAGE_QUALITY = 90

type TargetWithoutXXS = Exclude<Target, "xxs">
type Sizes = { base: string } & { [key in TargetWithoutXXS]?: string }

const toFormattedMinWidthSize = (target: TargetWithoutXXS, size?: string) => {
  return size ? `(min-width: ${BREAKPOINTS[target].minWidth}px) ${size}, ` : ""
}

/** Returns responsive sizes formatted for Next/Image. */
const createSizes = ({ base, xs, sm, md, lg, xl }: Sizes) => {
  const formattedXL = toFormattedMinWidthSize("xl", xl)
  const formattedLG = toFormattedMinWidthSize("lg", lg)
  const formattedMD = toFormattedMinWidthSize("md", md)
  const formattedSM = toFormattedMinWidthSize("sm", sm)
  const formattedXS = toFormattedMinWidthSize("xs", xs)

  // Order from largest to smallest
  return `${formattedXL}${formattedLG}${formattedMD}${formattedSM}${formattedXS}${base}`
}

export type ImageProps = {
  ref?: Ref<HTMLSpanElement>
  image: ImageType

  /**
   * This should be used if an image is less than 100% width of the screen.
   * Under the hood this utilizes Next/Image to serve image that is closest to
   * the size given at the current breakpoint.
   *
   * If image doesn't have sizes, you can pass `false` to disable it. This
   * should only be done if you're sure that the image will always be 100% width
   * or that it's an SVG.
   */
  imageSizes: Sizes | false
  imageProps?: Omit<
    ComponentPropsWithRef<"img">,
    "src" | "width" | "height" | "priority" | "loading" | "sizes" | "onLoad"
  >
  onLoad?: (image: HTMLImageElement) => void
} & Omit<ComponentPropsWithoutRef<"span">, "onLoad"> &
  SharedAssetProps

export const Image = ({
  ref,
  image,
  imageSizes,
  imageProps,
  onLoad: propOnLoad,
  hasPriority = false,
  loading = "lazy",
  objectFit = "cover",
  animateOnLoad: animateOnLoadProp,
  lazyLoadBackgroundColor,
  ...rest
}: ImageProps) => {
  const containerRef = useRef<HTMLSpanElement>(null)

  const onLoad = useCallback(
    (event: SyntheticEvent<HTMLImageElement>) => {
      if (propOnLoad) propOnLoad(event.currentTarget)

      const container = containerRef.current
      if (container) container.setAttribute("data-is-loaded", "true")
    },
    [propOnLoad],
  )

  const formattedSizes = useMemo(() => {
    return imageSizes ? createSizes(imageSizes) : undefined
  }, [imageSizes])

  const animateOnLoad = useMemo(() => {
    return hasPriority && animateOnLoadProp === undefined
      ? false
      : (animateOnLoadProp ?? true)
  }, [hasPriority, animateOnLoadProp])

  const mergedContainerRefs = useMemo(() => {
    return mergeRefs(containerRef, ref)
  }, [containerRef, ref])

  return (
    <span
      {...rest}
      ref={mergedContainerRefs}
      className={cn(s.assetContainer, rest.className)}
      style={
        {
          "--lazy-load-background-color": lazyLoadBackgroundColor,
          ...rest.style,
        } as CSSProperties
      }
      data-is-image
      data-has-lazy-load-background-color={
        lazyLoadBackgroundColor !== false && animateOnLoad
      }
      {...(animateOnLoad && { "data-animate-on-load": true })}
    >
      <NextImage
        alt={image.alt ?? ""}
        draggable="false"
        quality={DEFAULT_IMAGE_QUALITY}
        // Here image props can only override the props above but not the
        // things after - this is deliberate
        {...imageProps}
        className={imageProps ? cn(s.asset, imageProps.className) : s.asset}
        style={imageProps ? { objectFit, ...imageProps.style } : { objectFit }}
        src={image.url}
        {...(image.width && image.height
          ? { width: image.width, height: image.height }
          : { fill: true })}
        priority={hasPriority}
        loading={hasPriority ? "eager" : loading}
        sizes={formattedSizes}
        onLoad={onLoad}
      />
    </span>
  )
}
